import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, Loader2, ArrowLeft, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLogin = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast.error(t('Veuillez entrer le mot de passe', 'Bitte Passwort eingeben'));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        // Store auth in session
        sessionStorage.setItem('adminAuth', btoa(`admin:${password}`));
        toast.success(t('Connexion réussie !', 'Erfolgreich angemeldet!'));
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(t('Mot de passe incorrect', 'Falsches Passwort'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-6">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-yellow-400/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Back link */}
        <Link 
          to="/"
          className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors"
          data-testid="admin-back-link"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('Retour au site', 'Zurück zur Website')}
        </Link>

        {/* Login Card */}
        <div className="bg-slate-800 border border-slate-700 rounded-3xl p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white font-outfit">
              {t('Administration', 'Verwaltung')}
            </h1>
            <p className="text-slate-400 mt-2">
              {t('Connectez-vous pour accéder au tableau de bord', 'Melden Sie sich an, um auf das Dashboard zuzugreifen')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">
                {t('Mot de passe', 'Passwort')}
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-12 h-14 rounded-xl bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
                  data-testid="admin-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              size="lg"
              disabled={loading}
              className="w-full btn-primary rounded-full py-6 text-base font-bold"
              data-testid="admin-login-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                  {t('Connexion...', 'Anmeldung...')}
                </>
              ) : (
                <>
                  <Lock className="mr-2 w-5 h-5" />
                  {t('Se connecter', 'Anmelden')}
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-8">
          © 2026 – {t("Un billet, une valise et l'Allemagne", "Ein Ticket, ein Koffer und Deutschland")}
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
